import React, { useState, useEffect } from "react";
import {
  FaCheck,
  FaCalendar,
  FaMapMarkerAlt,
  FaSpinner,
  FaClock,
  FaRedo,
  FaMicrophone,
  FaBuilding,
  FaTimes,
  FaHistory,
  FaUser,
} from "react-icons/fa";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import DescriptionViewer from "../../../Common/DescriptionViewer";
import ConferenceSubNav from "../../../Common/ConferenceSubNav";

const NewFreeConferance = () => {
  const navigate = useNavigate();
  const [conferences, setConferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const [processingConferenceId, setProcessingConferenceId] = useState(null);
  const [confirmConferenceId, setConfirmConferenceId] = useState(null);
  const [isConfirmChecked, setIsConfirmChecked] = useState(false);
  const [rejectConferenceId, setRejectConferenceId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [isRejectChecked, setIsRejectChecked] = useState(false);

  const stats = {
    newInvites: conferences.length,
  };

  const fetchConferences = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/get/new/conferences`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const data = response.data?.data || response.data;
      setConferences(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      console.error("Error fetching conferences:", err);
      if (err.response && err.response.status === 403) {
        toast.error(err.response.data?.message || "Verification Required.");
      } else {
        setError("Failed to load invitations.");
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConferences();
  }, []);

  const handleAccept = async (conferenceId) => {
    setProcessingConferenceId(conferenceId);
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/accept/conference/${conferenceId}`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (res.status === 200) {
        toast.success("Accepted!");
        await fetchConferences();
        setTimeout(() => {
          navigate("/reporter/freeconference/accepted");
        }, 1500);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Acceptance failed");
    } finally {
      setProcessingConferenceId(null);
      setConfirmConferenceId(null);
      setIsConfirmChecked(false);
    }
  };

  const handleRejected = async (conferenceId, reason) => {
    setProcessingConferenceId(conferenceId);
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/reject/conference/${conferenceId}`,
        { rejectNote: reason },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (res.status === 200) {
        toast.success("Rejected!");
        await fetchConferences();
        setTimeout(() => {
          navigate("/reporter/freeconference/rejected");
        }, 1500);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Rejection failed");
    } finally {
      setProcessingConferenceId(null);
      setRejectConferenceId(null);
      setRejectReason("");
      setIsRejectChecked(false);
    }
  };

  const sortedConferences = [...conferences].sort((a, b) => {
    const dateA = new Date(a.submittedAt || a.createdAt).getTime();
    const dateB = new Date(b.submittedAt || b.createdAt).getTime();
    return dateB - dateA;
  });

  const totalPages = Math.ceil(sortedConferences.length / rowsPerPage);
  const paginatedConferences = sortedConferences.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] pb-24 text-slate-900">
      {/* Premium Compact Header */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-slate-200/50 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="px-3 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-600 text-[10px] font-black uppercase tracking-tight hover:bg-slate-100 transition-all whitespace-nowrap shadow-sm"
            >
              ← Go Back
            </button>
            <div className="w-1.5 h-8 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full" />
            <div>
              <h1 className="text-lg font-black text-slate-900 leading-tight">Media Hub</h1>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider leading-none">New Assignments</span>
                <div className="w-1 h-1 rounded-full bg-slate-300" />
                <span className="text-[9px] font-black text-blue-600 italic leading-none">{stats.newInvites} Invitations</span>
              </div>
            </div>
          </div>
          <button
            onClick={fetchConferences}
            disabled={loading}
            className="w-9 h-9 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400 active:rotate-180 transition-all duration-500"
          >
            <FaRedo className={`text-[10px] ${loading ? "animate-spin text-blue-500" : ""}`} />
          </button>
        </div>
      </div>

      <ConferenceSubNav type="free" />

      <div className="max-w-5xl mx-auto p-3 space-y-3">
        {/* Statistics - Ultra Compact */}
        {!loading && !error && (
          <div className="grid grid-cols-1 gap-2">
            <div className="bg-slate-900 rounded-2xl p-4 text-white relative overflow-hidden group">
              <div className="absolute -right-4 -bottom-4 w-12 h-12 bg-white/5 rounded-full blur-xl group-hover:bg-white/10 transition-all" />
              <p className="text-[8px] font-black text-slate-400 uppercase mb-1 tracking-widest leading-none">Total New Intel</p>
              <p className="text-sm font-black tracking-tighter tabular-nums text-blue-400 leading-none">{stats.newInvites}</p>
            </div>
          </div>
        )}

        {loading && (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="relative">
              <div className="w-10 h-10 border-4 border-blue-100 border-t-blue-500 rounded-full animate-spin" />
              <FaMicrophone className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-blue-500 text-[8px]" />
            </div>
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Intercepting Invites...</p>
          </div>
        )}

        {/* Desktop View: Table (Ultra-Compact) */}
        {!loading && !error && conferences.length > 0 && (
          <div className="hidden lg:block bg-white rounded-[1.5rem] shadow-sm border border-slate-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50/50 text-slate-400 border-b border-slate-50">
                <tr>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Reference</th>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Topic Intel</th>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Location</th>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Schedule</th>
                  <th className="px-5 py-4 text-center text-[9px] font-black uppercase tracking-widest">Response</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {paginatedConferences.map((conference) => (
                  <tr key={conference._id} className="hover:bg-slate-50/30 transition-colors">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center border border-blue-100/50">
                          <FaMicrophone className="text-blue-600 text-[10px]" />
                        </div>
                        <span className="font-black text-slate-400 text-[10px] tabular-nums tracking-tighter italic">#{conference.conferenceId}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <p className="text-[10px] font-black text-slate-700 italic truncate max-w-[200px] leading-tight">{conference.topic || "Classified Subject"}</p>
                      <div className="mt-1">
                        <DescriptionViewer description={conference.purpose} wordLimit={5} textSize="text-[8px]" buttonText="Intel Brief" modalTitle="Mission Objective" />
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <p className="text-[10px] font-black text-slate-800 leading-none">{conference.city}, {conference.state}</p>
                      <p className="text-[8px] font-bold text-slate-400 uppercase leading-none mt-1">{conference.place}</p>
                    </td>
                    <td className="px-5 py-3 text-[10px] text-slate-400 font-bold italic tabular-nums leading-none">
                      <div className="flex flex-col gap-1">
                        <span className="text-slate-600">{new Date(conference.conferenceDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                        <span className="text-slate-400">{conference.conferenceTime} {conference.timePeriod}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <button onClick={() => setConfirmConferenceId(conference.conferenceId)} className="h-8 px-4 bg-slate-900 text-white rounded-xl text-[9px] font-black uppercase tracking-widest active:scale-95 transition-all">Accept</button>
                        <button onClick={() => setRejectConferenceId(conference.conferenceId)} className="w-8 h-8 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-all"><FaTimes className="text-[10px]" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Mobile View: High-Density Widgets */}
        {!loading && !error && (
          <div className="lg:hidden max-w-md mx-auto space-y-3">
            {conferences.length > 0 ? (
              paginatedConferences.map((conference) => (
                <div key={conference._id} className="relative bg-white rounded-[1.75rem] border border-slate-100 p-1 shadow-sm active:scale-[0.99] transition-all overflow-hidden border-b-2 border-b-blue-400/20">
                  <div className="bg-slate-50/40 rounded-[1.6rem] p-3.5 flex flex-col gap-3">
                    {/* Header */}
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-blue-500 to-indigo-600 p-0.5 shadow-md shadow-blue-100/50 flex-shrink-0">
                        <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                          <FaMicrophone className="text-blue-500 text-sm" />
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-[12px] font-black text-slate-900 leading-tight truncate pr-2 uppercase italic">{conference.topic || "Classified News"}</h3>
                        <div className="flex items-center justify-between mt-0.5">
                          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest tabular-nums italic">Ref #{conference.conferenceId}</p>
                          <span className="px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest bg-blue-100 text-blue-800">Pending Invite</span>
                        </div>
                      </div>
                    </div>

                    {/* Quick Info Grid */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-white rounded-xl p-2.5 border border-slate-100/50 flex flex-col gap-1">
                        <p className="text-[7px] font-black text-slate-300 uppercase leading-none">Schedule</p>
                        <p className="text-[9px] font-black text-slate-700 italic truncate">{new Date(conference.conferenceDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} @ {conference.conferenceTime}</p>
                      </div>
                      <div className="bg-white rounded-xl p-2.5 border border-slate-100/50 flex flex-col gap-1">
                        <p className="text-[7px] font-black text-slate-300 uppercase leading-none">Location</p>
                        <p className="text-[9px] font-black text-slate-700 italic truncate">{conference.city || "Remote Intel"}</p>
                      </div>
                    </div>

                    {/* Actions Group */}
                    <div className="flex items-center gap-2">
                      <button onClick={() => setConfirmConferenceId(conference.conferenceId)} className="flex-1 h-11 rounded-xl bg-slate-900 text-white shadow-lg shadow-slate-200/50 flex items-center justify-center gap-2 active:scale-95 transition-all text-[10px] font-black uppercase tracking-widest">Accept Spot</button>
                      <button onClick={() => setRejectConferenceId(conference.conferenceId)} className="w-11 h-11 rounded-xl bg-white border border-slate-200 text-slate-400 flex items-center justify-center active:bg-rose-50 active:text-rose-500 transition-colors shadow-sm"><FaTimes className="text-xs" /></button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-24 px-10 text-center">
                <div className="w-16 h-16 rounded-[2rem] bg-white shadow-lg flex items-center justify-center mb-6">
                  <FaHistory className="text-slate-100 text-3xl" />
                </div>
                <h2 className="text-base font-black text-slate-900 tracking-tight">Radar Clear</h2>
                <p className="text-[9px] font-bold text-slate-400 mt-2 px-6 uppercase tracking-wider italic leading-relaxed text-center">No new coverage invitations detected in your sector.</p>
              </div>
            )}
          </div>
        )}

        {/* Compact Pagination Bar */}
        {!loading && !error && sortedConferences.length > rowsPerPage && (
          <div className="flex items-center justify-center gap-2 py-4">
            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="w-9 h-9 rounded-xl bg-white border border-slate-100 flex items-center justify-center text-slate-400 disabled:opacity-20 shadow-sm transition-all"><FaRedo className="-rotate-90 text-[9px]" /></button>
            <div className="h-9 px-4 rounded-xl bg-slate-900 flex items-center gap-2 shadow-xl shadow-slate-100">
              <span className="text-[10px] font-black text-white italic">{currentPage}</span>
              <div className="w-1 h-1 rounded-full bg-slate-700" />
              <span className="text-[10px] font-black text-slate-500 italic">{totalPages}</span>
            </div>
            <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="w-9 h-9 rounded-xl bg-white border border-slate-100 flex items-center justify-center text-slate-400 disabled:opacity-20 shadow-sm transition-all"><FaRedo className="rotate-90 text-[9px]" /></button>
          </div>
        )}
      </div>

      {/* Decline Intel Modal */}
      {rejectConferenceId && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={() => setRejectConferenceId(null)} />
          <div className="relative w-full max-w-sm bg-white rounded-[2.5rem] p-7 shadow-2xl animate-in zoom-in-95 duration-300">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-rose-50 text-rose-500 rounded-full flex items-center justify-center mx-auto mb-4 text-xl">
                <FaTimes />
              </div>
              <h2 className="text-xl font-black text-slate-900 tracking-tighter">Decline Intel?</h2>
              <p className="text-[9px] font-black text-slate-400 mt-1 uppercase tracking-wider italic leading-none">Confirm refusal of sector coverage</p>
            </div>

            <textarea
              className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl p-4 text-[11px] font-bold text-slate-700 placeholder:text-slate-300 focus:outline-none focus:border-rose-100 transition-all mb-4"
              rows={2}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for bypassing mission..."
            />

            <div className="flex items-center gap-3 mb-6 px-1" onClick={() => setIsRejectChecked(!isRejectChecked)}>
              <div className={`w-5 h-5 rounded-lg flex items-center justify-center transition-all ${isRejectChecked ? 'bg-rose-500 text-white' : 'bg-slate-100 border-2 border-slate-200 text-transparent'}`}><FaCheck className="text-[8px]" /></div>
              <label className="text-[9px] font-black text-slate-500 uppercase tracking-tighter select-none cursor-pointer">Confirm mission termination</label>
            </div>

            <div className="flex items-center gap-2">
              <button onClick={() => handleRejected(rejectConferenceId, rejectReason)} disabled={!isRejectChecked || !rejectReason.trim() || processingConferenceId} className="flex-[2] h-12 rounded-xl bg-rose-500 text-white text-[10px] font-black uppercase tracking-widest active:scale-95 disabled:opacity-20 transition-all shadow-lg shadow-rose-100">Abort Intel</button>
              <button onClick={() => setRejectConferenceId(null)} className="flex-1 h-12 rounded-xl bg-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-widest active:bg-slate-200 transition-all">Return</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Intel Modal */}
      {confirmConferenceId && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={() => setConfirmConferenceId(null)} />
          <div className="relative w-full max-w-sm bg-white rounded-[2.5rem] p-7 shadow-2xl animate-in zoom-in-95 duration-300 overflow-hidden border border-white/50">
            <div className="absolute -top-16 -right-16 w-32 h-32 bg-blue-50 rounded-full blur-3xl opacity-70" />
            <div className="text-center relative z-10 mb-6">
              <div className="w-20 h-20 bg-gradient-to-tr from-blue-500 to-indigo-600 text-white rounded-[2.2rem] flex items-center justify-center mx-auto mb-5 shadow-xl shadow-blue-200">
                <FaCheck className="text-2xl" />
              </div>
              <h2 className="text-xl font-black text-slate-900 tracking-tighter">Secure Spot</h2>
              <p className="text-[10px] font-black text-slate-400 mt-2 px-6 uppercase tracking-wider leading-relaxed italic">Confirm coverage deployment for this intel record.</p>
            </div>

            <div className="flex items-center gap-3 mb-6 bg-blue-50/50 p-4 rounded-2xl border border-blue-100/50 relative z-10" onClick={() => setIsConfirmChecked(!isConfirmChecked)}>
              <div className={`w-6 h-6 rounded-lg flex items-center justify-center transition-all shrink-0 ${isConfirmChecked ? 'bg-blue-600 text-white shadow-md' : 'bg-white border-2 border-blue-100 text-transparent'}`}><FaCheck className="text-[10px]" /></div>
              <label className="text-[10px] font-black text-blue-800 uppercase italic tracking-tighter leading-tight select-none cursor-pointer">I commit to official reporting standards</label>
            </div>

            <div className="flex flex-col gap-2 relative z-10">
              <button onClick={() => handleAccept(confirmConferenceId)} disabled={!isConfirmChecked || processingConferenceId} className="w-full h-14 rounded-2xl bg-slate-900 text-white text-[11px] font-black uppercase tracking-widest shadow-xl shadow-slate-200 active:scale-95 transition-all disabled:opacity-30">
                {processingConferenceId ? <FaSpinner className="animate-spin text-lg mx-auto" /> : "Initiate Deployment"}
              </button>
              <button onClick={() => setConfirmConferenceId(null)} className="w-full h-10 rounded-xl bg-white text-[9px] font-black text-slate-400 uppercase tracking-widest transition-colors hover:text-slate-600">Re-Inspect Record</button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-center" theme="dark" hideProgressBar />
    </div>
  );
};

export default NewFreeConferance;
